/**
 * Enhanced pricing calculation hook that uses pricing templates/rules
 * instead of hardcoded defaults to provide accurate customer pricing
 */

import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { useCompany } from "../../../hooks/useCompany";
import { useSafeProductForm } from "../PricingCalculatorHelpers";
import { CUSTOMS_CLEARANCE } from "../../../lib/pricingDefaults";

export interface PricingComponentResult {
  id: number;
  componentId: number;
  name: string;
  description: string | null;
  componentType: string;
  calculationType: string;
  value: number;
  currency: string;
  quantity: number;
  totalPrice: number;
  isOptional: boolean;
  isEnabled: boolean;
  canCustomerEdit: boolean;
  categoryGroup: "transport" | "customs" | "handling" | "delivery" | "other";
}

export interface TemplatePricingResult {
  components: PricingComponentResult[];
  totalCost: number;
  currency: string;
  matchedTemplate: any | null;
  matchingCriteria: string[];
  error: string | null;
}

interface BookingData {
  totalCartons: number;
  totalPallets: number;
  uniqueProducts: number;
  totalWeight: number;
  totalVolume: number;
  containerSize?: number;
  uniqueHsCodesCount: number;
}

export const useTemplatePricing = () => {
  const { state, dispatch } = useBookingWizard();
  const { selectedCompany } = useCompany();
  const { currentFormValues, isEditingProduct } = useSafeProductForm();
  
  const [pricingResult, setPricingResult] = useState<TemplatePricingResult>({
    components: [],
    totalCost: 0,
    currency: "EUR",
    matchedTemplate: null,
    matchingCriteria: [],
    error: null,
  });

  const [loading, setLoading] = useState(false);
  const [componentOverrides, setComponentOverrides] = useState<
    Record<number, { enabled: boolean; value?: number }>
  >({});

  /**
   * Calculate booking data from current state
   */
  const calculateBookingData = (): BookingData => {
    let products = state.products || [];
    
    // Include currently editing product if exists
    if (isEditingProduct && currentFormValues) {
      products = [...products, currentFormValues as any];
    }

    const totalCartons = products.reduce((sum, p) => sum + (p.cartons || 0), 0);
    const totalPallets = products.reduce((sum, p) => sum + (p.pallets || 0), 0);
    const uniqueProducts = products.length;
    const totalWeight = products.reduce((sum, p) => sum + (p.weight || 0), 0);
    const totalVolume = products.reduce((sum, p) => sum + (p.cbm || 0), 0);
    const uniqueHsCodes = new Set(
      products
        .map((p) => {
          const code = (p as any).hs_code ?? (p as any).hsCode;
          if (code === null || code === undefined) return null;
          const normalized = String(code).trim();
          return normalized.length > 0 ? normalized : null;
        })
        .filter((code) => code !== null)
    );
    const uniqueHsCodesCount = uniqueHsCodes.size;

    return {
      totalCartons,
      totalPallets,
      uniqueProducts,
      totalWeight,
      totalVolume,
      containerSize: state.container_id || undefined,
      uniqueHsCodesCount,
    };
  };

  /**
   * Find matching pricing template for current booking
   */
  const findMatchingTemplate = async () => {
    try {
      const matchDate = state.origin_pickup_date || new Date().toISOString().split("T")[0];

      // Get container size for FCL bookings
      let containerSize = null;
      if (state.booking_type === 2 && state.container_id) {
        const { data: container } = await supabase
          .from("containers")
          .select("container_size")
          .eq("id", state.container_id)
          .single();
        
        containerSize = container?.container_size || null;
      }

      // Find ALL matching pricing templates (same logic as admin)
      const { data: allTemplates, error: templatesError } = await supabase
        .from("pricing_templates")
        .select("*")
        .eq("is_active", true)
        .lte("valid_from", matchDate)
        .or(`valid_to.is.null,valid_to.gte.${matchDate}`);

      if (templatesError) throw templatesError;

      if (!allTemplates || allTemplates.length === 0) {
        return { templates: [], bestTemplate: null, matchingCriteria: [] };
      }

      // Score and rank templates by specificity
      const scoredTemplates = allTemplates
        .map((template) => {
          let score = 0;
          const matchingCriteria: string[] = [];

          // Check transport type
          if (template.transport_type === state.transport_type) {
            score += 10;
            matchingCriteria.push("Transport Type");
          } else if (template.transport_type !== null) {
            return null; // Mismatch
          }

          // Check booking type
          if (template.booking_type === state.booking_type) {
            score += 10;
            matchingCriteria.push("Booking Type");
          } else if (template.booking_type !== null) {
            return null; // Mismatch
          }

          // Check incoterm
          if (template.incoterm === state.booking_incoterm) {
            score += 10;
            matchingCriteria.push("Incoterm");
          } else if (template.incoterm !== null) {
            return null; // Mismatch
          }

          // Check origin port
          if (template.origin_port === state.origin_port) {
            score += 10;
            matchingCriteria.push("Origin Port");
          } else if (template.origin_port !== null) {
            return null; // Mismatch
          }

          // Check destination port
          if (template.destination_port === state.destination_port) {
            score += 10;
            matchingCriteria.push("Destination Port");
          } else if (template.destination_port !== null) {
            return null; // Mismatch
          }

          // Check container size (for FCL)
          if (state.booking_type === 2 && template.container_size === containerSize) {
            score += 10;
            matchingCriteria.push("Container Size");
          } else if (template.container_size !== null) {
            return null; // Mismatch
          }

          return { template, score, matchingCriteria };
        })
        .filter((item) => item !== null && item.score > 0);

      if (scoredTemplates.length === 0) {
        return { templates: [], bestTemplate: null, matchingCriteria: [] };
      }

      // Sort by score (highest first)
      scoredTemplates.sort((a, b) => b!.score - a!.score);
      
      // Return all matching templates (for merging components)
      // Best match is first in the array
      return {
        templates: scoredTemplates.map(s => s!.template),
        bestTemplate: scoredTemplates[0]!.template,
        matchingCriteria: scoredTemplates[0]!.matchingCriteria,
      };
    } catch (error) {
      console.error("Error finding matching template:", error);
      return { templates: [], bestTemplate: null, matchingCriteria: [] };
    }
  };

  /**
   * Get template components with inherited values
   */
  const getTemplateComponents = async (templateId: number) => {
    try {
        const { data: components, error } = await supabase
        .from("pricing_template_components")
        .select(`
          *,
          pricing_components (*)
        `)
        .eq("template_id", templateId)
        .eq("is_active", true);

      if (error) throw error;

      // Check for parent template inheritance
      const { data: template } = await supabase
        .from("pricing_templates")
        .select("parent_template_id")
        .eq("id", templateId)
        .single();

      if (template?.parent_template_id) {
        const parentComponents = await getTemplateComponents(template.parent_template_id);
        
        // Merge parent and child components (child overrides parent)
        const childComponentIds = new Set(
          components?.map((c) => c.component_id) || []
        );
        
        const inheritedComponents = parentComponents.filter(
          (pc: any) => !childComponentIds.has(pc.component_id)
        );
        
        return [...(components || []), ...inheritedComponents];
      }

      return components || [];
    } catch (error) {
      console.error("Error getting template components:", error);
      return [];
    }
  };

  /**
   * Get company-specific pricing overrides
   */
  const getCompanyOverrides = async () => {
    if (!selectedCompany) return [];

    try {
      const { data, error } = await supabase
        .from("company_pricing_overrides")
        .select("*")
        .eq("company_id", selectedCompany);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error getting company overrides:", error);
      return [];
    }
  };

  /**
   * Determine component category for grouping
   */
  const categorizeComponent = (
    componentType: string,
    name: string
  ): "transport" | "customs" | "handling" | "delivery" | "other" => {
    const lowerName = name.toLowerCase();
    const lowerType = componentType.toLowerCase();

    if (
      lowerName.includes("transport") ||
      lowerName.includes("freight") ||
      lowerName.includes("shipping") ||
      lowerType.includes("transport")
    ) {
      return "transport";
    }

    if (
      lowerName.includes("customs") ||
      lowerName.includes("clearance") ||
      lowerName.includes("duty")
    ) {
      return "customs";
    }

    if (
      lowerName.includes("handling") ||
      lowerName.includes("warehouse") ||
      lowerName.includes("storage")
    ) {
      return "handling";
    }

    if (lowerName.includes("delivery") || lowerName.includes("pickup")) {
      return "delivery";
    }

    return "other";
  };

  /**
   * Determine if component is optional (customer can toggle)
   */
  const isComponentOptional = (
    componentType: string,
    calculationType: string,
    name: string
  ): boolean => {
    const lowerName = name.toLowerCase();
    
    // Optional components that customers can choose to include
    const optionalKeywords = [
      "insurance",
      "inspection",
      "fumigation",
      "storage",
      "license",
      "certificate",
      "additional",
      "extra",
      "optional",
    ];

    return optionalKeywords.some((keyword) => lowerName.includes(keyword));
  };

  /**
   * Determine if customer can edit the value
   */
  const canCustomerEditValue = (
    componentType: string,
    calculationType: string,
    name: string
  ): boolean => {
    const lowerName = name.toLowerCase();
    
    // Components where we might not know the exact value
    const editableKeywords = [
      "pickup",
      "storage",
      "additional",
      "extra",
      "custom",
    ];

    return editableKeywords.some((keyword) => lowerName.includes(keyword));
  };

  /**
   * Calculate component value based on calculation type
   */
  const calculateComponentValue = (
    component: any,
    bookingData: BookingData,
    baseValue: number
  ): { quantity: number; totalPrice: number } => {
    const calculationType = component.pricing_components?.calculation_type;
    let quantity = 1;
    let totalPrice = baseValue;

    switch (calculationType) {
      case "per_carton":
        quantity = bookingData.totalCartons;
        totalPrice = baseValue * quantity;
        break;

      case "per_pallet":
        quantity = bookingData.totalPallets;
        totalPrice = baseValue * quantity;
        break;

      case "per_unique_product": {
        // Tiered flat fee — bill qty 1; totalPrice already includes extras
        const count = bookingData.uniqueHsCodesCount;
        quantity = 1;
        if (component.included_quantity && count > component.included_quantity) {
          const extraProducts = count - component.included_quantity;
          const extraCost = (component.extra_price || 0) * extraProducts;
          totalPrice = baseValue + extraCost;
        } else {
          totalPrice = baseValue;
        }
        break;
      }

      case "per_kg":
        quantity = bookingData.totalWeight;
        totalPrice = baseValue * quantity;
        break;

      case "per_cbm":
        // For FCL Seafreight, ignore volume-based calculations (same as calculator)
        const isFCL = state.booking_type === 2; // FCL = 2
        const isSeafreight = state.transport_type === 2; // Seafreight = 2
        if (isFCL && isSeafreight) {
          quantity = 0;
          totalPrice = 0;
        } else {
          quantity = bookingData.totalVolume;
          totalPrice = baseValue * quantity;
        }
        break;

      case "per_shipment":
      case "fixed":
        quantity = 1;
        totalPrice = baseValue;
        break;

      case "per_container":
        quantity = 1;
        totalPrice = baseValue;
        break;

      case "percentage":
        // For percentage-based, we need a base value from other components
        // This is complex and might need special handling
        quantity = 1;
        totalPrice = baseValue;
        break;

      default:
        quantity = 1;
        totalPrice = baseValue;
    }

    return { quantity, totalPrice };
  };

  /**
   * Apply company override to component value
   */
  const applyCompanyOverride = (
    componentId: number,
    baseValue: number,
    overrides: any[]
  ): { value: number; isDisabled: boolean } => {
    const override = overrides.find((o) => o.pricing_component_id === componentId);

    if (!override) {
      return { value: baseValue, isDisabled: false };
    }

    switch (override.override_type) {
      case "fixed_price":
        return {
          value: override.fixed_value ?? baseValue,
          isDisabled: false,
        };

      case "percentage_adjustment":
        const adjustment = (override.percentage_adjustment || 100) / 100;
        return {
          value: baseValue * adjustment,
          isDisabled: false,
        };

      case "disabled":
        return {
          value: 0,
          isDisabled: true,
        };

      default:
        return { value: baseValue, isDisabled: false };
    }
  };

  /**
   * Calculate pricing from template
   */
  const calculatePricing = async () => {
    setLoading(true);

    try {
      // Find matching templates (all matches, not just best)
      const { templates, bestTemplate, matchingCriteria } = await findMatchingTemplate();

      if (!bestTemplate || templates.length === 0) {
        setPricingResult({
          components: [],
          totalCost: 0,
          currency: "EUR",
          matchedTemplate: null,
          matchingCriteria: [],
          error: "No matching pricing template found for this booking configuration",
        });
        setLoading(false);
        return;
      }

      // Merge components from all matching templates (prioritizing higher-scoring templates)
      // Same logic as calculator tool
      const componentMap = new Map<number, any>(); // Track by component ID to avoid duplicates
      const templateScores = new Map<number, number>(); // Track template scores
      
      // Calculate scores for each template
      templates.forEach((template, index) => {
        let score = 0;
        if (template.transport_type === state.transport_type) score += 10;
        if (template.booking_type === state.booking_type) score += 10;
        if (template.incoterm === state.booking_incoterm) score += 10;
        if (template.origin_port === state.origin_port) score += 10;
        if (template.destination_port === state.destination_port) score += 10;
        templateScores.set(template.id, score);
      });

      // Sort templates by score (highest first)
      const sortedTemplates = [...templates].sort((a, b) => {
        const scoreA = templateScores.get(a.id) || 0;
        const scoreB = templateScores.get(b.id) || 0;
        return scoreB - scoreA;
      });

      // Merge components from all templates
      for (const template of sortedTemplates) {
        const templateComponents = await getTemplateComponents(template.id);
        const templateScore = templateScores.get(template.id) || 0;

        templateComponents.forEach((tc: any) => {
          const componentId = tc.component_id;
          const existingComponent = componentMap.get(componentId);

          if (!existingComponent) {
            // New component - add it with template info
            componentMap.set(componentId, {
              ...tc,
              template_name: template.name,
              template_score: templateScore,
            });
          } else {
            // Component exists - keep the one from the higher scoring template
            if (templateScore > existingComponent.template_score) {
              componentMap.set(componentId, {
                ...tc,
                template_name: template.name,
                template_score: templateScore,
              });
            }
          }
        });
      }

      const components = Array.from(componentMap.values());

      if (components.length === 0) {
        setPricingResult({
          components: [],
          totalCost: 0,
          currency: bestTemplate.currency || "EUR",
          matchedTemplate: bestTemplate,
          matchingCriteria,
          error: "No components found in pricing template",
        });
        setLoading(false);
        return;
      }

      // Get company overrides
      const companyOverrides = await getCompanyOverrides();

      // Calculate booking data
      const bookingData = calculateBookingData();

      // Process each component
      const processedComponents: PricingComponentResult[] = [];
      let currency = bestTemplate.currency || "EUR";
      const isStorageDelivery = state.delivery_type === "storage";

      const isFCL = state.booking_type === 2;
      const isSeafreight = state.transport_type === 2;
      const isAirFreight = state.transport_type === 1;

      for (const tc of components) {
        if (!tc.pricing_components) continue;
        // Don't skip components with null value - calculate them as 0 (same as calculator)
        // if (tc.value === null) continue;

        const component = tc.pricing_components;
        const componentId = tc.component_id;

        // Filter by booking type: FCL = Seafreight FCL only, LCL = Seafreight LCL + Airfreight, Both = all
        const appliesToBookingType = component.applies_to_booking_type ?? "both";
        if (appliesToBookingType !== "both") {
          if (isAirFreight && appliesToBookingType === "fcl") continue;
          if (isSeafreight) {
            if (appliesToBookingType === "fcl" && !isFCL) continue;
            if (appliesToBookingType === "lcl" && isFCL) continue;
          }
        }

        // Filter by unloading location (derived from delivery type)
        // FCL + Delivery = at_location (direct to customer, geen lossingskosten)
        // FCL + Storage = at_efreight (goods at E-Freight, wel lossingskosten)
        // LCL/Airfreight = at_efreight (always)
        const appliesToUnloading =
          component.applies_to_unloading_location ?? "both";
        if (appliesToUnloading !== "both") {
          const effectiveUnloading =
            isStorageDelivery
              ? "at_efreight"
              : isFCL && isSeafreight
                ? "at_location"
                : "at_efreight";
          if (appliesToUnloading !== effectiveUnloading) continue;
        }

        // Apply company override
        // Handle null values same as calculator (use 0 as default)
        const baseValue = tc.value !== null ? tc.value : 0;
        const { value: adjustedValue, isDisabled } = applyCompanyOverride(
          componentId,
          baseValue,
          companyOverrides
        );

        if (isDisabled) continue;

        // Calculate quantity and total
        const { quantity, totalPrice } = calculateComponentValue(
          tc,
          bookingData,
          adjustedValue
        );

        // Determine component properties
        const isOptional = isComponentOptional(
          component.component_type,
          component.calculation_type,
          component.name
        );

        const canEdit = canCustomerEditValue(
          component.component_type,
          component.calculation_type,
          component.name
        );

        const categoryGroup = categorizeComponent(
          component.component_type,
          component.name
        );

        // Check if customer has overridden this component
        const override = componentOverrides[componentId];
        let isEnabled = override ? override.enabled : !isOptional || true;
        let finalValue = override?.value !== undefined ? override.value : adjustedValue;
        let finalTotal = override?.value !== undefined
          ? calculateComponentValue(tc, bookingData, override.value).totalPrice
          : totalPrice;

        const isExportLicenseComponent =
          component.component_type?.toLowerCase().includes("license") ||
          component.name.toLowerCase().includes("license");

        if (!state.includeExportClearanceLicense && isExportLicenseComponent) {
          isEnabled = false;
          finalValue = 0;
          finalTotal = 0;
        }

        if (isStorageDelivery && categoryGroup === "delivery") {
          isEnabled = false;
          finalValue = 0;
          finalTotal = 0;
        }

        processedComponents.push({
          id: tc.id,
          componentId,
          name: component.name,
          description: component.description,
          componentType: component.component_type,
          calculationType: component.calculation_type,
          value: finalValue,
          currency: tc.currency || currency,
          quantity,
          totalPrice: isEnabled ? finalTotal : 0,
          isOptional,
          isEnabled,
          canCustomerEdit: canEdit,
          categoryGroup,
        });
      }

      const hasCustomsPerUnique = processedComponents.some(
        (comp) => comp.categoryGroup === "customs" && comp.calculationType === "per_unique_product"
      );

      if (!hasCustomsPerUnique) {
        const extraHsCodes = Math.max(
          0,
          bookingData.uniqueHsCodesCount - CUSTOMS_CLEARANCE.INCLUDED_HS_CODES
        );

        if (extraHsCodes > 0) {
          const extraCost = extraHsCodes * CUSTOMS_CLEARANCE.EXTRA_HS_CODE_FEE;
          const customsComponent = processedComponents.find(
            (comp) => comp.categoryGroup === "customs" && comp.calculationType === "per_shipment"
          );

          if (customsComponent) {
            customsComponent.totalPrice += extraCost;
            customsComponent.value += extraCost;
          } else {
            processedComponents.push({
              id: -1,
              componentId: -1,
              name: "Extra HS codes",
              description: `Additional HS codes beyond ${CUSTOMS_CLEARANCE.INCLUDED_HS_CODES}`,
              componentType: "customs_clearance",
              calculationType: "per_shipment",
              value: extraCost,
              currency,
              quantity: 1,
              totalPrice: extraCost,
              isOptional: false,
              isEnabled: true,
              canCustomerEdit: false,
              categoryGroup: "customs",
            });
          }
        }
      }

      // Filter out components with zero totalPrice (same as calculator)
      // But keep optional components even if disabled (they can be enabled)
      const visibleComponents = processedComponents.filter((comp) => {
        // Filter out components with zero calculated value
        if (comp.totalPrice === 0 && !comp.isOptional) return false;
        
        // Filter out delivery components if storage delivery
        if (isStorageDelivery) {
          const isDeliveryComponent = 
            comp.componentType?.toLowerCase().includes("delivery") ||
            comp.name.toLowerCase().includes("delivery") ||
            comp.name.toLowerCase().includes("last mile");
          if (isDeliveryComponent && comp.totalPrice === 0) return false;
        }
        
        return true;
      });

      // Calculate total cost
      const totalCost = visibleComponents.reduce(
        (sum, c) => sum + (c.isEnabled ? c.totalPrice : 0),
        0
      );

      setPricingResult({
        components: visibleComponents,
        totalCost,
        currency,
        matchedTemplate: bestTemplate,
        matchingCriteria,
        error: null,
      });
    } catch (error) {
      console.error("Error calculating pricing:", error);
      setPricingResult({
        components: [],
        totalCost: 0,
        currency: "EUR",
        matchedTemplate: null,
        matchingCriteria: [],
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle component enabled/disabled
   */
  const toggleComponent = (componentId: number, enabled: boolean) => {
    setComponentOverrides((prev) => ({
      ...prev,
      [componentId]: {
        ...prev[componentId],
        enabled,
      },
    }));
  };

  /**
   * Update component value
   */
  const updateComponentValue = (componentId: number, value: number) => {
    setComponentOverrides((prev) => ({
      ...prev,
      [componentId]: {
        ...prev[componentId],
        value,
      },
    }));
  };

  // Recalculate when relevant state changes
  useEffect(() => {
    if (
      state.transport_type &&
      state.booking_type &&
      state.booking_incoterm &&
      state.origin_port &&
      state.destination_port
    ) {
      calculatePricing();
    }
  }, [
    state.transport_type,
    state.booking_type,
    state.booking_incoterm,
    state.origin_port,
    state.destination_port,
    state.container_id,
    state.products,
    state.origin_pickup_date,
    state.currentStep,
    state.delivery_type,
    selectedCompany,
    isEditingProduct,
    currentFormValues,
    componentOverrides,
  ]);

  return {
    pricingResult,
    loading,
    toggleComponent,
    updateComponentValue,
    recalculate: calculatePricing,
  };
};






